import { Field, useFormikContext } from "formik";
import React, { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { MESSAGE_OVERLOAD_ERROR, MONITORING_COMMAND, MONITORING_COMMAND_STATE } from "../../../../constants/command-status";
import Commands from '../../../../constants/commands';
import { MQTTHardwareSubCmds } from "../../../../constants/sub-commands";
import { useLocale } from "../../../../contexts/localeContext";
import { TranslationKeys } from "../../../../contexts/TranslationKeys";
import { useIcaWebsocketContext } from "../../../../contexts/websocket/IcaWebsocketContext";
import { Controller } from "../../../../models/Controller";
import { ExecuteCommand } from "../../../../models/ExecuteCommand";
import { ListQuery } from "../../../../models/ListQuery";
import { executeCommand } from "../../../../services/executeCommandService";
import { getCloudEnabledControllers } from "../../../../services/hardwareDeviceTypeService";
import { getErrorMessage } from "../../../../services/utilities/ServiceError";
import { IAppState } from "../../../../store/types";
import { validateControllerName, validateMacAddress } from "../../../../utilities/Validation";
import ActivityFrame from "../../../activity-frame/activity-frame";
import CheckboxField from "../../../checkbox-list-field/checkboxField";
import { useDialog } from "../../../dialog/DialogContext";
import Label from "../../../label/label";
import PaginatedSearchableSelectField from "../../../selection-field/paginated-searchable-select-field";
import {colonDashRegex} from "../../../../utilities/regex";

interface DetailCloudControllerProps {
    onNext:(e) => void;
    onBack:(e) => void;
    setSelectedDevice: (controller: Controller) => void;
    device: Controller;
    filterInstalled?: boolean;
}

const DetailCloudController: FC<DetailCloudControllerProps> = ({ onNext, onBack, setSelectedDevice, device, filterInstalled }) => {
    const {translate} = useLocale();
    const { errors, values, setFieldValue, isValid } = useFormikContext<any>();
    const icaWebSocket = useIcaWebsocketContext();
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const dialog = useDialog();
    const user = useSelector((state: IAppState) => (state.userInfo));
    const [busy, setBusy] = useState(false);
    const [busyMessage, setBusyMessage] = useState<string>('');
    const [errorPingingController, setErrorPingingController] = useState(false);

    useEffect(() => {
        if (icaWebSocket.connectionState) {
            icaWebSocket.startStopTopic(MONITORING_COMMAND.MONITORING_DISCOVERY, MONITORING_COMMAND.MONITORING, MONITORING_COMMAND_STATE.MONITORING_START);
            icaWebSocket.setOnMessage(onMessage);
        }
    }, [icaWebSocket.connectionState]);

    const handleSelection = (event, selectedOption) => {
        const updatedOptions = downstreamDevicesOptions.map((option) => {
            if (option.value === selectedOption.value) {
                const newSelected = !option.isSelected;
                if (option.value === 0) {
                    setFieldValue('detectDownstreamIpDevices', newSelected);
                } else {
                    setFieldValue('detectPeripheralsDirectly', newSelected);
                }
                return { ...option, isSelected: newSelected };
            }
            return option;
        });
        setDownstreamDevicesOptions(updatedOptions);
    };

    const onMessage = async (data) => {
        if (!data.data.includes(MESSAGE_OVERLOAD_ERROR)) {
            const response = JSON.parse(data.data);
            if (response.command === MONITORING_COMMAND.MONITORING_DISCOVERY) {
                const resData = response.attributes.discovery[0];
                if (resData.subCommand === MQTTHardwareSubCmds.MQTT_Controller_Ping) {
                    if (resData.totalDiscovered === 1 && resData.totalIPsToScan === 1) {
                        scanNetwork(resData.address, resData.macAddress);
                    } else {
                        setErrorPingingController(true);
                        setBusy(false);
                    }
                }
            }
        }
    }

    const isEnabled = () => {
        const requiredFieldsFilled = values.macAddress; // Only MAC address is required in order to ping/register.
        return (isValid && requiredFieldsFilled);
    }

    const handleBack = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        if (onBack) {
            setSelectedDevice({
                id: 0,
                name: '',
                autoCreateDoorsType: undefined,
                selectedPeripherals: [],
                selectIPDevices: [],
                detectDownstreamIpDevices: false,
                detectPeripheralsDirectly: false,
            });
            onBack(e);
        }
    };

    const handleNext = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>): void => {
        const fixedAddress = values.fixedAddress ? values.fixedAddress : getFixedAddress(values.macAddress);
        device.fixedAddress = fixedAddress;
        device.name = values.name;
        device.autoInstallPeripherals = true;
        device.typeName = values.typeName;
        device.typeId = values.typeId;

        setSelectedDevice({
            ...device,
            fixedAddress: fixedAddress,
            serial: values.serial,
            typeId: values.typeId,
            typeName: values.typeName,
            name: values.name,
            macAddress: values.macAddress,
            detectDownstreamIpDevices: values.detectDownstreamIpDevices,
            detectPeripheralsDirectly: values.detectPeripheralsDirectly,
        })

        pingController(e);
    };

    const [downstreamDevicesOptions, setDownstreamDevicesOptions] = useState([
        { label: translate.byKey(TranslationKeys.installDownstreamIpDevices), value: 0, isSelected: values.detectDownstreamIpDevices },
        { label: translate.byKey(TranslationKeys.detectPeripheralsDirectly), value: 1, isSelected: values.detectPeripheralsDirectly }
    ]);

    const getFixedAddress = (macAddress: string) => {
        if (macAddress.length < 10) {
            return macAddress;
        }
        const cleanedAddress = macAddress.replace(colonDashRegex, '');
        return cleanedAddress.substring(4).toUpperCase();
    }

    const pingController = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const command: ExecuteCommand = {
            cmd: Commands.Discovery.label,
            cmdSubType: MQTTHardwareSubCmds.MQTT_Controller_Ping,
            userLoginId: user.id,
            siteId: siteId,
            fixedAddress: values.fixedAddress ? values.fixedAddress : getFixedAddress(values.macAddress),
            macAddress: values.macAddress,
        }

        setBusyMessage(translate.byKey(TranslationKeys.authenticatingMessage));
        setBusy(true);
        executeCommand(command)
            .then((res) => {
                // Result of ping will be reported by websocket
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
                setErrorPingingController(true);
            })
            .finally(() => {
                // Let the on message handle the disposing of the busy loader
            })
    }

    const scanNetwork = (fixedAddress: string, macAddress: string) => {
        const command: ExecuteCommand = {
            cmd: Commands.Discovery.label,
            cmdSubType: MQTTHardwareSubCmds.MQTT_Discovery,
            userLoginId: user.id,
            siteId: siteId,
            fixedAddress: fixedAddress,
            macAddress: macAddress,
        }

        setBusyMessage(translate.byKey(TranslationKeys.searchingForDevicesMessage));
        executeCommand(command)
            .then((res) => {
                // Result of scan will be reported by websocket
            })
            .catch((error) => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) })
            })
            .finally(() => {
                setBusy(false);
                onNext({});
            })
    }

    /**
     * Editing the name and type does not persist, so we only show those fields if we selected an existing device.
     * Otherwise, we only need a MAC address to register a new device.
     * @returns true if the device has an id (is an existing device), false if we are registering a new device
     */
    const hasDevice = () => {
        return device.id > 0;
    }

    return (
        <div className="pt-3 pr-3 h-full relative">
            <ActivityFrame busy={busy} busyMessage={busyMessage} className={`flex-wrap ${busy ? 'h-full w-full justify-center' : ''} content-center`}>
                <div
                    className={`mb-3 text-portal-blue-primary-shade2 text-xl font-semibold`} >
                    {translate.byKey(TranslationKeys.controllerDetails)}
                </div>
                <div className="flex flex-col pr-3">
                    {hasDevice() ? <div className="pb-4">
                        <div className="flex flex-row justify-between" id='startIpLabelId'>
                            <Label
                                htmlFor="titleCloudControllerId"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.name)}:</Label>
                            {errors.name ? <span className='text-red-600 text-xs'>{errors.name}</span> : null}
                        </div>
                        <Field
                            type="text"
                            name="name"
                            autoFocus
                            disabled={true}
                            className={`flex flex-row items-center pr-2 pl-2 p-1 text-sm w-full bg-gray-input
                                ${hasDevice()
                                    ? 'text-gray-500 cursor-not-allowed'
                                    : 'text-gray-800'
                                }`}
                            validate={value => validateControllerName(value, translate)} />
                    </div> : null}
                    {hasDevice() ? <div className="pb-4">
                        <div className="flex flex-row justify-between" id='startIpLabelId'>
                            <Label
                                htmlFor="titleCloudControllerId"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.type)}:</Label>
                            {errors.typeName ? <span className='text-red-600 text-xs'>{errors.typeName}</span> : null}
                        </div>
                        <PaginatedSearchableSelectField
                            name={translate.byKey(TranslationKeys.type)}
                            key='typeId'
                            accessor='typeId'
                            dataMethod={(listParameters?: ListQuery) => getCloudEnabledControllers()}
                            searchAccessor={'typeName'}
                            required={true}
                            disabled={true}
                            onSelect={(_, value) => {
                                setFieldValue('typeName', value.label);
                                setFieldValue('typeId', value.value);
                            }}
                            selectedItemLabel={device.typeName} />
                    </div> : null}
                    <div className="pb-4">
                        <div className="flex flex-row justify-between" id='macAddressId'>
                            <Label
                                htmlFor="macAddressId"
                                className="text-xs text-gray-600 mt-1" required>{translate.byKey(TranslationKeys.macAddress)}:</Label>
                            {errors.macAddress ? <span className='text-red-600 text-xs'>{errors.macAddress}</span> : null}
                        </div>
                        <Field
                            type="text"
                            name="macAddress"
                            id="macAddressId"
                            required
                            disabled={hasDevice()}
                            className={`flex flex-row items-center pr-2 pl-2 p-1 text-sm w-full bg-gray-input
                                ${hasDevice()
                                    ? 'text-gray-500 cursor-not-allowed'
                                    : 'text-gray-800'
                                }`}
                            validate={(value: string) => validateMacAddress(value, translate)} />
                    </div>
                    <div className="pb-4">
                        <div className="flex flex-row justify-between" id='downstreamDevices'>
                            <Label
                                htmlFor="downstreamDevicesId"
                                className="text-xs text-gray-600 mt-1">{translate.byKey(TranslationKeys.downstreamDevices)}:</Label>
                        </div>
                        {downstreamDevicesOptions.map((option, i) => {
                            return (
                                <div key={`downstreamDevices_${i}`} className="flex">
                                    <span className="text-xs">
                                        <CheckboxField
                                            onSelect={(event, option) => handleSelection(event, option)}
                                            option={option}
                                            compact/>
                                    </span>
                                </div>
                            )
                        })}
                    </div>
                    {errorPingingController && <span className='text-red-600 text-xs'>{translate.byKey(TranslationKeys.pingControllerErrorMessage)}</span>}
                </div>
                <div className="flex justify-end w-full absolute bottom-0 pr-3 pb-3">
                    <button
                        className={`py-1 px-4 mr-2 mt-3 rounded-full secondary secondary-inverse w-20 h-10 bg-sidemenu-focus text-sm items-center`}
                        type="button"
                        onClick={e => handleBack(e)}>{translate.byKey(TranslationKeys.back)}</button>
                    <button
                        className={`py-1 px-5 mt-3 secondary  ${isEnabled() ? 'text-white bg-portal-blue-primary-shade2' : 'secondary-disabled cursor-not-allowed'}`}
                        type="button"
                        disabled={!isEnabled()}
                        onClick={handleNext}>{translate.byKey(TranslationKeys.next)}</button>
                </div>
            </ActivityFrame>
        </div>
    )
}
export default DetailCloudController
